import React, { useState, useEffect } from "react";
import { Card, Row, Col, Form, Button, Alert, ProgressBar, Badge, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PageTitle from "../../layouts/PageTitle";
import { 
  getDepositorByIdAction,
  getDepositorKycAction,
  submitKycDocumentAction,
  refreshDepositorStatusAction,
  clearErrors
} from "../../../store/actions/DepositorsActions";
import swal from "sweetalert";
import DepositorsService from '../../../services/DepositorsService';

const ManageDepositorKyc = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const { 
    data: currentDepositor, 
    kycData,
    loading, 
    error, 
    success 
  } = useSelector((state) => state.depositors);

  const [kycFormData, setKycFormData] = useState({});
  const [kycFileData, setKycFileData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [editingKyc, setEditingKyc] = useState(null); // Track which KYC is being edited

  useEffect(() => {
    if (id) {
      dispatch(getDepositorByIdAction(id));
      dispatch(getDepositorKycAction(id));
    }

    return () => {
      dispatch(clearErrors());
    };
  }, [dispatch, id]);

  const handleKycInputChange = (kycId, value) => {
    setKycFormData(prev => ({
      ...prev,
      [kycId]: value
    }));

    // Clear validation error
    if (validationErrors[kycId]) {
      setValidationErrors(prev => ({
        ...prev,
        [kycId]: ''
      }));
    }
  };

  const handleKycFileChange = (kycId, file) => {
    setKycFileData(prev => ({
      ...prev,
      [kycId]: file
    }));

    // Clear validation error
    if (validationErrors[kycId]) {
      setValidationErrors(prev => ({
        ...prev,
        [kycId]: ''
      }));
    }
  };

  const validateKycDocument = (kyc) => {
    const isFileUpload = kyc.kycType === 'File Upload';
    const value = isFileUpload ? kycFileData[kyc.kycId] : kycFormData[kyc.kycId];
    
    if (!value || (typeof value === 'string' && !value.trim())) {
      setValidationErrors(prev => ({
        ...prev,
        [kyc.kycId]: 'This field is required'
      }));
      return false;
    }
    return true;
  };

  const handleKycSubmit = (kyc) => {
    console.log('handleKycSubmit', kyc);
    
    if (!validateKycDocument(kyc)) {
      return;
    }

    const isFileUpload = kyc.kycType === 'File Upload';
    
    if (isFileUpload) {
      // For file uploads, we need to use FormData
      const formData = new FormData();
      formData.append('companyKycId', kyc.companyKycId);
      formData.append('kycFile', kycFileData[kyc.kycId]);
      
      console.log('Submitting KYC file:', kycFileData[kyc.kycId]);
      dispatch(submitKycDocumentAction(id, formData, true)); // true indicates file upload
      
      // Clear the file data
      setKycFileData(prev => ({
        ...prev,
        [kyc.kycId]: null
      }));
    } else {
      // For text submissions
      const kycSubmissionData = {
        companyKycId: kyc.companyKycId,
        value: kycFormData[kyc.kycId]
      };

      console.log('Submitting KYC data:', kycSubmissionData);
      dispatch(submitKycDocumentAction(id, kycSubmissionData, false)); // false indicates text submission
      
      // Clear the form data for this KYC
      setKycFormData(prev => ({
        ...prev,
        [kyc.kycId]: ''
      }));
    }

    // Clear edit mode after submission
    setEditingKyc(null);
  };

  const handleEditKyc = (kyc) => {
    // Enable edit mode for this KYC
    setEditingKyc(kyc.kycId);
    
    // Pre-populate form data with existing value
    if (kyc.kycType === 'File Upload') {
      // For files, we can't pre-populate but we can show current file info
      console.log('Editing file KYC:', kyc);
    } else {
      // For text, pre-populate with current value
      setKycFormData(prev => ({
        ...prev,
        [kyc.kycId]: kyc.value || kyc.submittedValue || ''
      }));
    }
  };


  const handleCancelEdit = (kycId) => {
    // Cancel edit mode
    setEditingKyc(null);
    // Clear form data
    setKycFormData(prev => ({
      ...prev,
      [kycId]: ''
    }));
    setKycFileData(prev => ({
      ...prev,
      [kycId]: null
    }));
    // Clear validation errors
    setValidationErrors(prev => ({
      ...prev,
      [kycId]: ''
    }));
  };

  // Download file handler for KYC file uploads
  const handleDownloadKycFile = async (kyc) => {
    try {
      console.log('kyckyckyc', kyc)
      const result = await DepositorsService.downloadKycFile(id, kyc.submissionId);
      if (!result.success) throw new Error(result.message);
      const blob = result.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = kyc.fileName || kyc.submittedValue || 'kyc-file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      swal('Error', err.message || 'Failed to download file', 'error');
    }
  };

  const handleRefreshStatus = () => {
    swal({
      title: "Refresh Status",
      text: "This will refresh the depositor's KYC status based on completed documents. Continue?",
      icon: "info",
      buttons: true,
    })
    .then((willRefresh) => {
      if (willRefresh) {
        dispatch(refreshDepositorStatusAction(id));
      }
    });
  };

  const getKycProgress = () => {
    if (!kycData || !Array.isArray(kycData) || kycData.length === 0) return 0;
    const completedCount = kycData.filter(kyc => kyc.isCompleted).length;
    return Math.round((completedCount / kycData.length) * 100);
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Pending': return 'warning';
      case 'Inactive': return 'danger';
      case 'Rejected': return 'danger';
      default: return 'light';
    }
  };

  if (!currentDepositor || Object.keys(currentDepositor).length === 0) {
    return (
      <div>
        <PageTitle activeMenu="Manage Documents" motherMenu="Finance" />
        <Alert variant="info">Loading depositor information...</Alert>
      </div>
    );
  }

  const kycProgress = getKycProgress();

  return (
    <div>
      <PageTitle activeMenu="Manage Documents" motherMenu="Finance" />
      
      {/* Depositor Information */}
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center w-100">
                <div className="flex-grow-1">
                  <h4 className="card-title mb-0">Documents Management - {currentDepositor.name}</h4>
                </div>
                <div className="d-flex gap-2 flex-shrink-0">
                  <Button variant="outline-primary" onClick={handleRefreshStatus}>
                    <i className="las la-sync-alt me-2"></i>Refresh Status
                  </Button>
                  <Button variant="secondary" onClick={() => navigate('/depositors')}>
                    <i className="las la-arrow-left me-2"></i>Back to List
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <Row className="mb-4">
                <Col md={6}>
                  <div className="border rounded p-3">
                    <h6 className="mb-3">Depositor Details</h6>
                    <div className="mb-2"><strong>Name:</strong> {currentDepositor.name}</div>
                    <div className="mb-2"><strong>Email:</strong> {currentDepositor.email}</div>
                    <div className="mb-2"><strong>Phone:</strong> {currentDepositor.phoneNo}</div>
                    <div className="mb-2"><strong>Company:</strong> {currentDepositor.companyName || '--'}</div>
                    <div className="mb-2">
                      <strong>Status:</strong>{' '}
                      <Badge bg={getStatusBadgeVariant(currentDepositor.status)}>
                        {currentDepositor.status}
                      </Badge>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="border rounded p-3">
                    <h6 className="mb-3">KYC Progress</h6>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span>Overall Progress</span>
                        <span className="fw-bold">{kycProgress}%</span>
                      </div>
                      <ProgressBar 
                        now={kycProgress} 
                        variant={kycProgress === 100 ? 'success' : kycProgress > 50 ? 'warning' : 'danger'}
                        style={{ height: '8px' }}
                      />
                    </div>
                    <div className="mb-2">
                      <strong>Total Documents:</strong> {Array.isArray(kycData) ? kycData.length : 0}
                    </div>
                    <div className="mb-2">
                      <strong>Completed:</strong> {Array.isArray(kycData) ? kycData.filter(kyc => kyc.isCompleted).length : 0}
                    </div>
                    <div className="mb-2">
                      <strong>Pending:</strong> {Array.isArray(kycData) ? kycData.filter(kyc => !kyc.isCompleted).length : 0}
                    </div>
                    {kycProgress === 100 && (
                      <Alert variant="success" className="mt-3 mb-0">
                        <small>
                          <i className="las la-check-circle me-2"></i>
                          All KYC documents completed. Status should be automatically updated to Active.
                        </small>
                      </Alert>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* KYC Documents */}
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
              <h4 className="card-title">Documents</h4>
            </Card.Header>
            <Card.Body className="p-0">
              {kycData && Array.isArray(kycData) && kycData.length > 0 ? (
                <div className="table-responsive">
                  <Table striped bordered>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Document Name</th>
                        <th>Document Type</th>
                        <th>Status</th>
                        <th>Submitted Value</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kycData.map((kyc, index) => (
                        <tr key={kyc.kycId || index}>
                          <td>{index + 1}</td>
                          <td>
                            <div className="fw-bold">{kyc.kycName}</div>
                          </td>
                          <td>
                            <div className="fw-bold">{kyc.kycType}</div>
                            {kyc.isRequired && (
                              <small className="text-danger">Required</small>
                            )}
                          </td>
                          <td>
                            <Badge bg={kyc.isCompleted ? 'success' : 'warning'}>
                              {kyc.isCompleted ? 'Completed' : 'Pending'}
                            </Badge>
                          </td>
                          <td>
                            {kyc.isCompleted && editingKyc !== kyc.kycId ? (
                              <div>
                                <div className="text-success">
                                  {kyc.kycType === 'File Upload' && kyc.submittedValue && kyc.companyKycId ? (
                                    <div>
                                      <Button
                                        size="sm"
                                        variant="link"
                                        className="p-0"
                                        onClick={() => handleDownloadKycFile(kyc)}
                                        title="Download file"
                                      >
                                        <i className="las la-download"></i> Download {kyc.kycName}
                                      </Button>
                                      <br />
                                    </div>
                                  ) : (
                                    kyc.value || kyc.submittedValue || 'Submitted'
                                  )}
                                </div>
                                <small className="text-muted">
                                  {kyc.submittedAt && new Date(kyc.submittedAt).toLocaleDateString()}
                                </small>
                              </div>
                            ) : editingKyc === kyc.kycId || !kyc.isCompleted ? (
                              <div>
                                {kyc.kycType === 'File Upload' ? (
                                  <div>
                                    <Form.Control
                                      type="file"
                                      size="sm"
                                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                      onChange={(e) => handleKycFileChange(kyc.kycId, e.target.files[0])}
                                      isInvalid={!!validationErrors[kyc.kycId]}
                                    />
                                    {kycFileData[kyc.kycId] && (
                                      <small className="text-success d-block mt-1">
                                        <i className="las la-check-circle"></i> {kycFileData[kyc.kycId].name}
                                      </small>
                                    )}
                                    {kyc.isCompleted && kyc.fileName && (
                                      <small className="text-muted d-block mt-1">
                                        Current: {kyc.fileName}
                                      </small>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    <Form.Control
                                      type="text"
                                      size="sm"
                                      placeholder={`Enter ${kyc.kycName}`}
                                      value={kycFormData[kyc.kycId] || ''}
                                      onChange={(e) => handleKycInputChange(kyc.kycId, e.target.value)}
                                      isInvalid={!!validationErrors[kyc.kycId]}
                                    />
                                    {kyc.isCompleted && (
                                      <small className="text-muted d-block mt-1">
                                        Editing: {kyc.value || kyc.submittedValue}
                                      </small>
                                    )}
                                  </div>
                                )}
                                {validationErrors[kyc.kycId] && (
                                  <div className="invalid-feedback d-block">
                                    {validationErrors[kyc.kycId]}
                                  </div>
                                )}
                              </div>
                            ) : null}
                          </td>
                          <td>
                            {editingKyc === kyc.kycId ? (
                              <div className="d-flex gap-1">
                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={() => handleKycSubmit(kyc)}
                                  disabled={loading}
                                  title="Save changes"
                                >
                                  <i className="las la-save me-1"></i>Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => handleCancelEdit(kyc.kycId)}
                                  title="Cancel editing"
                                >
                                  <i className="las la-times"></i>
                                </Button>
                              </div>
                            ) : !kyc.isCompleted ? (
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={() => handleKycSubmit(kyc)}
                                disabled={loading}
                              >
                                <i className="las la-upload me-1"></i>
                                {kyc.kycType === 'File Upload' ? 'Upload' : 'Submit'}
                              </Button>
                            ) : (
                              <div className="d-flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handleEditKyc(kyc)}
                                  title="Edit this KYC document"
                                >
                                  <i className="las la-edit me-1"></i>Edit
                                </Button>
                                {kyc.kycType === 'File Upload' && kyc.fileName && (
                                  <Button
                                    size="sm"
                                    variant="outline-info"
                                    onClick={() => {
                                      swal("File Info", `File: ${kyc.fileName}\nUploaded: ${kyc.submittedAt ? new Date(kyc.submittedAt).toLocaleString() : 'Unknown'}`, "info");
                                    }}
                                    title="View file details"
                                  >
                                    <i className="las la-eye"></i>
                                  </Button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Alert variant="info">
                  <i className="las la-info-circle me-2"></i>
                  No KYC requirements found for this depositor's company.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Instructions */}
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
              <h5 className="card-title">Instructions</h5>
            </Card.Header>
            <Card.Body>
              <Alert variant="info">
                <h6>KYC Process Guidelines:</h6>
                <ul className="mb-0">
                  <li>Fill in all required KYC documents to complete the verification process</li>
                  <li>Once all documents are submitted, the depositor status will automatically change to "Active"</li>
                  <li>Use the "Refresh Status" button to manually update the status after completing documents</li>
                  <li>Contact admin if you encounter any issues with document submission</li>
                </ul>
              </Alert>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ManageDepositorKyc;